import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { analyticsSearchedRides } from '@rideiq/api/services/Analytics';
import { getRides } from '@rideiq/api/services/Rides';
import * as Constants from '@rideiq/constants';
import { useTimezone } from '@rideiq/hooks/useTimezone';
import { createRidesURLParams, readRidesURLState } from '@rideiq/rides/hooks/ridesURLParams';
import type { GroupedRides, RidesQueryParams } from '@rideiq/rides/types';
import { formatDate } from '@rideiq/utils/helpers';
import { useInfiniteQuery } from '@tanstack/react-query';
import { isToday, isTomorrow } from 'date-fns';
import { debounce, differenceBy, groupBy } from 'lodash';

export const RIDES_QUERY_KEY = 'rides';

const getSortDirection = (state: string): 'asc' | 'desc' => (['all', 'requested'].includes(state) ? 'asc' : 'desc');

const getDateGroup = (date: Date) =>
  (isToday(date) && `TODAY ${formatDate(date, 'LL.dd.yy').toUpperCase()}`) ||
  (isTomorrow(date)
    ? `TOMORROW ${formatDate(date, 'LL.dd.yy').toUpperCase()}`
    : !isToday(date) && !isTomorrow(date) && formatDate(date, 'EEEE LL.dd.yy').toUpperCase());

const combineRides = (existingRides: GroupedRides, newRides: GroupedRides): GroupedRides => {
  const combinedRides = existingRides;

  for (const rideDay in newRides) {
    if (existingRides[rideDay]) {
      combinedRides[rideDay] = [
        ...existingRides[rideDay],
        ...differenceBy(newRides[rideDay], existingRides[rideDay], 'slug'),
      ];
    } else {
      combinedRides[rideDay] = newRides[rideDay];
    }
  }

  return combinedRides;
};

const buildApiParams = (params: RidesQueryParams, page: number) => ({
  perPage: params.perPage,
  page,
  ...(params.query && { q: params.query }),
  state: params.state,
  fromDate: params.fromDate,
  sortDir: params.sortDir,
});

export const useRidesQuery = () => {
  const location = useLocation();
  const locationRef = useRef(location);

  locationRef.current = location;
  const [, setURLSearchParams] = useSearchParams();
  const { getDateInTimezone, getEndOfDate } = useTimezone();
  const urlState = readRidesURLState(location.search, getEndOfDate);
  const initialState = urlState.state;

  const [queryParams, setQueryParams] = useState<RidesQueryParams>({
    page: 1,
    perPage: Constants.ITEM_LIMIT_PER_PAGE,
    query: urlState.query,
    state: initialState,
    sortDir: getSortDirection(initialState),
    ...(urlState.fromDate && { fromDate: urlState.fromDate }),
  });
  const queryParamsRef = useRef(queryParams);

  queryParamsRef.current = queryParams;

  const { data, isFetching, isError, isLoadingError, refetch, fetchNextPage, hasNextPage, dataUpdatedAt } =
    useInfiniteQuery({
      queryKey: [RIDES_QUERY_KEY, queryParams],
      queryFn: async ({ pageParam, signal }) => {
        const response = await getRides(buildApiParams(queryParams, pageParam as number), signal);

        if (response.type === 'error') {
          throw new Error(response.error.errors[0] ?? 'Failed to load rides');
        }

        if (queryParams.query) {
          analyticsSearchedRides(queryParams.query, response.data.trips?.length ?? 0);
        }

        return response.data;
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage, allPages) =>
        (lastPage.trips?.length ?? 0) === Constants.ITEM_LIMIT_PER_PAGE ? allPages.length + 1 : undefined,
    });

  const rides = useMemo<GroupedRides>(() => {
    if (!data) {
      return {};
    }

    return data.pages.reduce<GroupedRides>((acc, page) => {
      const newRides = (page.trips ?? []).filter((trip) => trip.passengers.length > 0);
      const newGroupedRides = groupBy(newRides, (ride) => getDateGroup(getDateInTimezone(ride.startsAt)));
      const fromDateGroup = queryParams.fromDate
        ? { [getDateGroup(getDateInTimezone(queryParams.fromDate)) || '']: [] }
        : {};

      return combineRides(acc, { ...fromDateGroup, ...newGroupedRides });
    }, {});
  }, [data, queryParams.fromDate, getDateInTimezone]);

  const rideCount = data?.pages.reduce((count, page) => count + (page.trips?.length ?? 0), 0) ?? 0;

  // Legacy semantics: "isLoading" tracks whether a full page came back (more rides likely
  // remain), driving the table's infinite-scroll loading footer — not raw network state.
  const isLoading = !isError && (data ? hasNextPage ?? false : true);

  const lastFetch = useMemo(() => (dataUpdatedAt ? new Date(dataUpdatedAt) : undefined), [dataUpdatedAt]);

  const handlePagination = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const handleQuery = useCallback(
    (query: string, state?: string, fromDate?: string | null, silentReFetching = false) => {
      if (!query || query.length >= Constants.MINIMUM_SEARCH_STRING_LENGTH || silentReFetching) {
        window.scrollTo(0, 0);
        setQueryParams((prevQueryParams) => ({
          query: query !== prevQueryParams.query ? query : prevQueryParams.query,
          page: 1,
          perPage: prevQueryParams.perPage,
          state: state || prevQueryParams.state,
          fromDate: fromDate !== prevQueryParams.fromDate ? fromDate : prevQueryParams.fromDate,
          sortDir: getSortDirection(state || prevQueryParams.state),
        }));

        return true;
      }

      return false;
    },
    []
  );

  const debounceHandleQuery = useMemo(() => debounce(handleQuery, 400), [handleQuery]);
  const skipURLWrite = useRef(false);
  const hasWrittenURL = useRef(false);

  useEffect(() => {
    return () => {
      debounceHandleQuery.cancel();
    };
  }, [debounceHandleQuery]);

  useEffect(() => {
    const nextURLState = readRidesURLState(location.search, getEndOfDate);
    const currentQueryParams = queryParamsRef.current;

    if (
      currentQueryParams.query === nextURLState.query &&
      currentQueryParams.state === nextURLState.state &&
      (currentQueryParams.fromDate || undefined) === nextURLState.fromDate
    ) {
      return;
    }

    if (handleQuery(nextURLState.query, nextURLState.state, nextURLState.fromDate, true)) {
      skipURLWrite.current = true;
    }
  }, [getEndOfDate, handleQuery, location.search]);

  // Only filter fields write to history; POPs and pagination must not create new entries.
  useEffect(() => {
    const replaceInitialURL = !hasWrittenURL.current;

    hasWrittenURL.current = true;

    if (skipURLWrite.current) {
      skipURLWrite.current = false;

      return;
    }

    const current = new URLSearchParams(locationRef.current.search);
    const next = createRidesURLParams(locationRef.current.search, queryParams);

    if (next.toString() === current.toString()) {
      return;
    }

    setURLSearchParams(next, replaceInitialURL ? { replace: true } : undefined);
  }, [queryParams.query, queryParams.state, queryParams.fromDate]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    rides,
    rideCount,
    queryParams,
    isLoading,
    isFetching,
    isError,
    isLoadingError,
    lastFetch,
    fetchRides: refetch,
    handlePagination,
    debounceHandleQuery,
  };
};
